import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  GitCompareArrows,
  TrendingDown,
  TrendingUp,
  Minus,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";
import { fmtMoney, fmtDays, fmtYears, fmtPct } from "@/lib/chart-utils";

function pct(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return Number(n);
}

function DeltaCell({ a, b, unit, inverse = false }) {
  const na = pct(a);
  const nb = pct(b);
  if (na == null || nb == null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const diff = nb - na;
  const pctChange = na !== 0 ? (diff / Math.abs(na)) * 100 : null;
  const Icon =
    diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  // "inverse" means an increase is BAD (e.g. jours/an sacrifiés). Color accordingly.
  const isWorse = inverse ? diff > 0 : diff < 0;
  const color =
    diff === 0
      ? "text-muted-foreground"
      : isWorse
      ? "text-rose-600"
      : "text-emerald-600";
  return (
    <div className={`flex items-center gap-1 font-mono text-xs ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      <span>
        {diff > 0 ? "+" : ""}
        {diff.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}
        {unit ? ` ${unit}` : ""}
      </span>
      {pctChange != null && (
        <span className="opacity-75">
          ({diff > 0 ? "+" : ""}
          {pctChange.toLocaleString("fr-FR", {
            maximumFractionDigits: 1,
          })}
          %)
        </span>
      )}
    </div>
  );
}

function Row({ label, a, b, format, unit, inverse }) {
  return (
    <tr
      className="border-b border-border last:border-0"
      data-testid={`compare-row-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <td className="p-3 text-sm font-semibold">{label}</td>
      <td className="p-3 font-mono text-sm">{format(a)}</td>
      <td className="p-3 font-mono text-sm">{format(b)}</td>
      <td className="p-3">
        <DeltaCell a={a} b={b} unit={unit} inverse={inverse} />
      </td>
    </tr>
  );
}

export default function CompareProjects() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const aId = sp.get("a");
  const bId = sp.get("b");

  const [loading, setLoading] = useState(true);
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);

  const load = useCallback(async () => {
    if (!aId || !bId) {
      toast.error("Deux projets sont requis pour la comparaison.");
      navigate("/app/analyses", { replace: true });
      return;
    }
    setLoading(true);
    try {
      const [pa, pb, aa, ab] = await Promise.all([
        getProject(aId),
        getProject(bId),
        getLatestAnalysis(aId),
        getLatestAnalysis(bId),
      ]);
      setLeft({ project: pa, analysis: aa });
      setRight({ project: pb, analysis: ab });
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger la comparaison."
      );
    } finally {
      setLoading(false);
    }
  }, [aId, bId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = useMemo(
    () =>
      left?.analysis?.results?.summary?.currency ||
      right?.analysis?.results?.summary?.currency ||
      "UM",
    [left, right]
  );

  // Order chronologically (earliest year on the left)
  const [earlier, later] = useMemo(() => {
    if (!left || !right) return [left, right];
    const ya = left.project?.fiscal_year || 0;
    const yb = right.project?.fiscal_year || 0;
    return ya <= yb ? [left, right] : [right, left];
  }, [left, right]);

  if (loading || !earlier || !later) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="compare-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  const ra = earlier.analysis?.results || {};
  const rb = later.analysis?.results || {};
  const ia = ra.profiles?.[0]?.individual || {};
  const ib = rb.profiles?.[0]?.individual || {};

  // Trend chart data — 4 headline metrics
  const chartData = [
    {
      metric: "Jours/an",
      [earlier.project?.fiscal_year || "A"]: ia.jours_vie_sacrifies_par_an || 0,
      [later.project?.fiscal_year || "B"]: ib.jours_vie_sacrifies_par_an || 0,
    },
    {
      metric: "% vie fiscale",
      [earlier.project?.fiscal_year || "A"]: ia.ratio_vie_fiscale_pct || 0,
      [later.project?.fiscal_year || "B"]: ib.ratio_vie_fiscale_pct || 0,
    },
    {
      metric: "Années carrière",
      [earlier.project?.fiscal_year || "A"]: ia.annees_vie_sur_carriere || 0,
      [later.project?.fiscal_year || "B"]: ib.annees_vie_sur_carriere || 0,
    },
    {
      metric: "Années dette",
      [earlier.project?.fiscal_year || "A"]:
        ra.debt?.annees_vie_hypothequees || 0,
      [later.project?.fiscal_year || "B"]:
        rb.debt?.annees_vie_hypothequees || 0,
    },
  ];

  const keyA = String(earlier.project?.fiscal_year || "Exercice A");
  const keyB = String(later.project?.fiscal_year || "Exercice B");
  const differentCountry =
    (earlier.project?.country || "").toLowerCase() !==
    (later.project?.country || "").toLowerCase();

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Comparaison d'exercices
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <GitCompareArrows className="h-7 w-7 text-brand-blue dark:text-brand-gold" />
          {earlier.project?.country || "—"} ·{" "}
          <span className="font-mono">{keyA}</span>
          <span className="mx-2 text-muted-foreground">vs</span>
          <span className="font-mono">{keyB}</span>
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Évolution des KPIs vitalistes d'un exercice sur l'autre. Un{" "}
          <strong>accroissement</strong> des jours sacrifiés ou de la dette
          hypothéquée est coloré en <span className="text-rose-600">rouge</span>{" "}
          (défavorable au contribuable), une{" "}
          <strong>diminution</strong> en{" "}
          <span className="text-emerald-600">vert</span>.
        </p>
      </div>

      {differentCountry && (
        <div
          className="rounded-md border border-amber-400/40 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100"
          data-testid="compare-different-country-warning"
        >
          Les deux projets concernent des pays différents (
          {earlier.project?.country} vs {later.project?.country}). La lecture
          des écarts reste indicative.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {[earlier, later].map((side, idx) => (
          <Card
            key={idx}
            className={idx === 1 ? "border-brand-gold/40 bg-brand-gold/5" : ""}
            data-testid={`compare-side-${idx}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {side.project?.country}{" "}
                <span className="text-brand-gold">
                  · {side.project?.fiscal_year || "—"}
                </span>
              </CardTitle>
              <div className="text-xs text-muted-foreground">
                {side.project?.name}
                {side.analysis?.created_at &&
                  ` · analysé le ${new Date(
                    side.analysis.created_at
                  ).toLocaleDateString("fr-FR")}`}
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 pt-2 text-xs">
              <Badge variant="outline" className="justify-center py-2">
                {fmtDays(side.analysis?.results?.profiles?.[0]?.individual?.jours_vie_sacrifies_par_an)}{" "}
                jours/an
              </Badge>
              <Badge variant="outline" className="justify-center py-2">
                {fmtYears(
                  side.analysis?.results?.profiles?.[0]?.individual
                    ?.annees_vie_sur_carriere
                )}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend chart */}
      <Card data-testid="compare-chart-card">
        <CardHeader>
          <CardTitle className="text-base">
            Évolution des KPIs vitalistes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) =>
                    Number(v).toLocaleString("fr-FR", {
                      maximumFractionDigits: 2,
                    })
                  }
                />
                <Legend />
                <Bar dataKey={keyA} fill="#1A3C5E" radius={[4, 4, 0, 0]} />
                <Bar dataKey={keyB} fill="#C9A84C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detail table */}
      <Card data-testid="compare-detail-card">
        <CardHeader>
          <CardTitle className="text-base">
            Détail indicateur par indicateur
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3 font-semibold">Indicateur</th>
                  <th className="p-3 font-semibold">{keyA}</th>
                  <th className="p-3 font-semibold">{keyB}</th>
                  <th className="p-3 font-semibold">Évolution</th>
                </tr>
              </thead>
              <tbody>
                <Row
                  label="Jours/an sacrifiés"
                  a={ia.jours_vie_sacrifies_par_an}
                  b={ib.jours_vie_sacrifies_par_an}
                  format={(v) => fmtDays(v)}
                  unit="jours"
                  inverse
                />
                <Row
                  label="% vie fiscale"
                  a={ia.ratio_vie_fiscale_pct}
                  b={ib.ratio_vie_fiscale_pct}
                  format={(v) => fmtPct(v)}
                  unit="pts"
                  inverse
                />
                <Row
                  label="Total prélèvements/an"
                  a={ia.total_prelevements}
                  b={ib.total_prelevements}
                  format={(v) => fmtMoney(v, currency)}
                  unit={currency}
                  inverse
                />
                <Row
                  label="Années carrière sacrifiées"
                  a={ia.annees_vie_sur_carriere}
                  b={ib.annees_vie_sur_carriere}
                  format={(v) => fmtYears(v)}
                  unit="années"
                  inverse
                />
                <Row
                  label="Salaire journalier"
                  a={ia.salaire_journalier}
                  b={ib.salaire_journalier}
                  format={(v) => fmtMoney(v, currency)}
                  unit={currency}
                />
                <Row
                  label="Dette / contribuable"
                  a={ra.debt?.dette_par_contribuable}
                  b={rb.debt?.dette_par_contribuable}
                  format={(v) => fmtMoney(v, currency)}
                  unit={currency}
                  inverse
                />
                <Row
                  label="Années vie hypothéquées"
                  a={ra.debt?.annees_vie_hypothequees}
                  b={rb.debt?.annees_vie_hypothequees}
                  format={(v) => fmtYears(v)}
                  unit="années"
                  inverse
                />
                <Row
                  label="Budget national (années cumulées)"
                  a={ra.national?.annees_vies_budget}
                  b={rb.national?.annees_vies_budget}
                  format={(v) => fmtYears(v)}
                  unit="années"
                />
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() => navigate("/app/analyses")}
          data-testid="compare-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour aux analyses
        </Button>
      </div>
    </div>
  );
}
